import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  mapApiCustomerContactToUiRow,
  mapMutationContactToUiRows,
  mergeContactUpdateIntoList,
} from "api-client";
import type { CustomerDetailContactsListQueryPatch } from "design-components";
import { customerDetailInitialState } from "./customer-detail.models";
import {
  associateCustomerLocation,
  createCustomerContact,
  deleteCustomerLocation,
  fetchCustomerContact,
  fetchCustomerContactRoles,
  fetchCustomerContacts,
  fetchCustomerLocations,
  fetchCustomerOverview,
  inviteCustomerUser,
  uploadCustomerLogo,
  updateCustomerContact,
  updateCustomerLocation,
  updateCustomerStatus,
} from "./customer-detail.thunk";

const customerDetailSlice = createSlice({
  name: "customerDetail",
  initialState: customerDetailInitialState,
  reducers: {
    setSelectedCustomerId: (state, action: PayloadAction<string | null>) => {
      if (state.selectedCustomerId !== action.payload) {
        state.customer = null;
        state.users = [];
        state.error = null;
        state.locations = [];
        state.locationsSearch = "";
        state.locationsCanSearch = false;
        state.locationsError = null;
      }
      state.selectedCustomerId = action.payload;
    },
    clearCustomerDetail: (state) => {
      state.customer = null;
      state.users = [];
      state.locations = [];
      state.locationsSearch = "";
      state.locationsCanSearch = false;
      state.contacts = [];
      state.contactsTotalCount = 0;
      state.contactRoles = [];
      state.contactsError = null;
      state.contactEditDetail = null;
      state.contactEditContactId = null;
      state.contactEditLoading = false;
      state.error = null;
      state.locationsError = null;
      state.selectedCustomerId = null;
    },
    clearCustomerDetailError: (state) => {
      state.error = null;
    },
    setCustomerLocationsSearch: (state, action: PayloadAction<string>) => {
      state.locationsSearch = action.payload;
    },
    setCustomerContactsListQuery: (state, action: PayloadAction<CustomerDetailContactsListQueryPatch>) => {
      state.contactsListQuery = { ...state.contactsListQuery, ...action.payload };
    },
    clearContactEditDetail: (state) => {
      state.contactEditLoading = false;
      state.contactEditContactId = null;
      state.contactEditDetail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.customer = action.payload.data.customer;
        state.users = action.payload.data.customer.users;
      })
      .addCase(fetchCustomerOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to fetch customer overview";
        state.customer = null;
        state.users = [];
      })
      .addCase(updateCustomerStatus.pending, (state) => {
        state.statusSubmitting = true;
      })
      .addCase(updateCustomerStatus.fulfilled, (state, action) => {
        state.statusSubmitting = false;
        if (state.customer) {
          state.customer.status = action.payload.data.status;
        }
      })
      .addCase(updateCustomerStatus.rejected, (state) => {
        state.statusSubmitting = false;
      })
      .addCase(fetchCustomerLocations.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchCustomerLocations.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = null;
        state.locations = action.payload.data.locations;
        const term = action.meta.arg.search?.trim() ?? "";
        state.locationsSearch = term;
        if (!term) {
          state.locationsCanSearch = state.locations.length > 0;
        }
      })
      .addCase(fetchCustomerLocations.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload ?? "Failed to fetch customer locations";
        state.locations = [];
      })
      .addCase(associateCustomerLocation.fulfilled, (state, action) => {
        const row = action.payload.data;
        const exists = state.locations.some((loc) => loc.locationId === row.locationId);
        if (!exists) {
          state.locations = [...state.locations, row];
        }
        state.locationsCanSearch = true;
      })
      .addCase(updateCustomerLocation.fulfilled, (state, action) => {
        const row = action.payload.data;
        state.locations = state.locations.map((loc) =>
          loc.locationId === row.locationId ? row : loc,
        );
      })
      .addCase(deleteCustomerLocation.fulfilled, (state, action) => {
        const locationId = action.meta.arg.locationId;
        state.locations = state.locations.filter((loc) => loc.locationId !== locationId);
        if (!state.locationsSearch.trim() && state.locations.length === 0) {
          state.locationsCanSearch = false;
        }
      })
      .addCase(inviteCustomerUser.pending, (state) => {
        state.userInviteSubmitting = true;
      })
      .addCase(inviteCustomerUser.fulfilled, (state, action) => {
        state.userInviteSubmitting = false;
        const user = action.payload;
        const exists = state.users.some((u) => u.userId === user.userId);
        if (!exists) {
          state.users = [...state.users, user];
        }
        if (state.customer) {
          const customerHasUser = state.customer.users.some((u) => u.userId === user.userId);
          if (!customerHasUser) {
            state.customer.users = [...state.customer.users, user];
          }
        }
      })
      .addCase(inviteCustomerUser.rejected, (state) => {
        state.userInviteSubmitting = false;
      })
      .addCase(uploadCustomerLogo.pending, (state) => {
        state.logoUploading = true;
      })
      .addCase(uploadCustomerLogo.fulfilled, (state, action) => {
        state.logoUploading = false;
        if (state.customer) {
          state.customer.logoUrl = action.payload.data.companyLogoUrl;
        }
      })
      .addCase(uploadCustomerLogo.rejected, (state) => {
        state.logoUploading = false;
      })
      .addCase(fetchCustomerContactRoles.fulfilled, (state, action) => {
        state.contactRoles = action.payload.data?.roles ?? [];
      })
      .addCase(fetchCustomerContacts.pending, (state) => {
        state.contactsLoading = true;
        state.contactsError = null;
      })
      .addCase(fetchCustomerContacts.fulfilled, (state, action) => {
        state.contactsLoading = false;
        state.contactsError = null;
        state.contactsTotalCount = action.payload.data.totalCount;
        state.contacts = action.payload.data.contacts.map(mapApiCustomerContactToUiRow);
      })
      .addCase(fetchCustomerContacts.rejected, (state, action) => {
        state.contactsLoading = false;
        state.contactsError = action.payload ?? "Failed to fetch customer contacts";
        state.contacts = [];
        state.contactsTotalCount = 0;
      })
      .addCase(fetchCustomerContact.pending, (state, action) => {
        state.contactEditLoading = true;
        state.contactEditContactId = action.meta.arg.contactId;
        state.contactEditDetail = null;
      })
      .addCase(fetchCustomerContact.fulfilled, (state, action) => {
        const requestedContactId = action.meta.arg.contactId;
        if (requestedContactId !== state.contactEditContactId) {
          return;
        }
        if (action.payload.data.contactId !== requestedContactId) {
          return;
        }
        state.contactEditLoading = false;
        state.contactEditDetail = action.payload.data;
      })
      .addCase(fetchCustomerContact.rejected, (state, action) => {
        if (action.meta.arg.contactId !== state.contactEditContactId) {
          return;
        }
        state.contactEditLoading = false;
        state.contactEditContactId = null;
        state.contactEditDetail = null;
      })
      .addCase(createCustomerContact.pending, (state) => {
        state.contactFormSubmitting = true;
      })
      .addCase(createCustomerContact.fulfilled, (state, action) => {
        state.contactFormSubmitting = false;
        const rows = mapMutationContactToUiRows(action.payload.data);
        if (rows.some((row) => row.isPrimary)) {
          state.contacts = state.contacts.map((contact) => ({ ...contact, isPrimary: false }));
        }
        state.contacts = [...state.contacts, ...rows];
        state.contactsTotalCount += 1;
      })
      .addCase(createCustomerContact.rejected, (state) => {
        state.contactFormSubmitting = false;
      })
      .addCase(updateCustomerContact.pending, (state) => {
        state.contactFormSubmitting = true;
      })
      .addCase(updateCustomerContact.fulfilled, (state, action) => {
        state.contactFormSubmitting = false;
        const contactId = action.meta.arg.contactId;
        const updatedRows = mapMutationContactToUiRows(action.payload.data);
        const previousRowCount = state.contacts.filter((contact) => contact.contactId === contactId).length;

        if (updatedRows.some((row) => row.isPrimary)) {
          state.contacts = state.contacts.map((contact) => ({ ...contact, isPrimary: false }));
        }

        state.contacts = mergeContactUpdateIntoList(state.contacts, contactId, updatedRows);
        state.contactsTotalCount = Math.max(0, state.contactsTotalCount - previousRowCount + 1);

        if (state.contactEditDetail?.contactId === contactId) {
          state.contactEditDetail = action.payload.data;
        }
      })
      .addCase(updateCustomerContact.rejected, (state) => {
        state.contactFormSubmitting = false;
      });
  },
});

export const {
  setSelectedCustomerId,
  clearCustomerDetail,
  clearCustomerDetailError,
  setCustomerLocationsSearch,
  setCustomerContactsListQuery,
  clearContactEditDetail,
} = customerDetailSlice.actions;
export default customerDetailSlice.reducer;
